import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/errors/use-toast';
import { createTask, getMinCardOrder } from '../database-tasks';
import { Task } from '@/types/tasks';
import { TaskInsert, mapDbTaskToUiTask } from '../types';
import { updateGoalCompletionStatus } from '@/hooks/goal-planner/database-goals';
import { useUser } from '@/hooks/auth/use-user';
import { QuestTriggers } from '@/hooks/onboarding/useQuestDetection';
import { supabase } from '@/integrations/supabase/client';
import { useTaskContextTracking } from '@/hooks/tasks/useTaskContextTracking';

/**
 * Custom hook for adding a new task
 */
export const useAddTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { trackTaskChange } = useTaskContextTracking();
  
  return useMutation({
    mutationFn: async (task: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => {
      if (!userId) throw new Error('User not authenticated');
      
      // Get minimum card order to place at the bottom
      const minOrder = await getMinCardOrder(userId);
      
      // Determine the correct user_id for the task
      let taskUserId = userId;
      
      // If task is linked to a goal, use the goal owner's user_id for proper RLS access
      // This ensures both owners and editors can see tasks in shared projects
      if (task.goalId) {
        try {
          const { data: goalData, error: goalError } = await supabase
            .from('planner_goals')
            .select('user_id')
            .eq('id', task.goalId)
            .single();
          
          if (goalError) {
            console.error('Failed to fetch goal owner:', goalError);
            // Fallback to current user if goal lookup fails
          } else if (goalData?.user_id) {
            taskUserId = goalData.user_id; // Use goal owner's ID for RLS compatibility
          }
        } catch (error) {
          console.error('Error looking up goal owner:', error);
          // Fallback to current user if any error occurs
        }
      }
      
      const taskInsert: TaskInsert = {
        title: task.title,
        description: task.description || null,
        due_date: task.dueDate || null,
        priority: task.priority || null,
        urgency: task.urgency || null,
        is_completed: false,
        user_id: taskUserId, // Use goal owner's ID for collaborative tasks
        card_order: task.cardOrder !== undefined ? task.cardOrder : minOrder,
        goal_id: task.goalId || null
      };

      const { data, error } = await createTask(taskInsert);
      
      if (error) throw new Error(error.message);
      return data ? mapDbTaskToUiTask(data) : null;
    },
    onSuccess: async (data) => {
      // Invalidate all task queries for this user to refresh the data
      // Use partial matching to invalidate all task queries regardless of date range
      queryClient.invalidateQueries({ 
        queryKey: ['tasks', userId],
        exact: false 
      });
      
      // Update goal completion status if task is linked to a goal
      if (data?.goalId && user?.id) {
        try {
          await updateGoalCompletionStatus(data.goalId, user.id);
          // Invalidate goal queries to reflect updated completion status
          queryClient.invalidateQueries({ 
            queryKey: ['goals', user.id],
            exact: false 
          });
        } catch (error) {
          console.error('Failed to update goal completion status:', error);
          // Don't fail the whole operation for this
        }
      }
      
      // Track the task creation for context if task has a goal
      if (data?.id && data?.goalId) {
        await trackTaskChange(data.id, 'create');
        console.log('[useAddTaskMutation] Tracked task creation for context');
      }
      
      // Trigger quest completion for task creation (Step 5)
      QuestTriggers.taskAdded();
      
      toast({
        title: "Task added",
        description: "Your task has been added successfully",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Error adding task",
        description: error.message || "Failed to add task",
        variant: "error",
      });
    }
  });
};
